import { alterarProprietarioSchema } from '../utils/functions'
import { useSnackbar } from '@tecsinapse/cortex-react'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
  AlterarProprietarioInput,
  AlterarProprietarioMutationMutation,
  AlterarProprietarioMutationMutationVariables,
  ItemBiblioteca
} from '@/core/types/graphql'
import { zodResolver } from '@hookform/resolvers/zod'
import { ALTERAR_PROPRIETARIO } from '../data/alterar-proprietario'
import { t } from '@lingui/core/macro'
import { useCallback, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { request } from '../../../core/utils/request'
import { getGraphQLErrorMessage } from '@/core/utils/graphql-error'
import { useParams } from 'react-router'

export type AlterarProprietarioValues = {
  proprietario: string
}

export const useAlterarProprietarioDialog = ({
  pastaAtual,
  close
}: {
  pastaAtual: ItemBiblioteca
  close: () => void
}) => {
  const { snackbar } = useSnackbar()
  const queryClient = useQueryClient()
  const params = useParams()

  const alterarProprietarioMutation = useMutation({
    mutationKey: ['alterarProprietario'],
    mutationFn: async (
      variables: AlterarProprietarioMutationMutationVariables
    ) =>
      await request<
        AlterarProprietarioMutationMutation,
        AlterarProprietarioMutationMutationVariables
      >(ALTERAR_PROPRIETARIO, { variables }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['itemsPorPasta', params.idPasta]
      })
    }
  })

  const initialValues: AlterarProprietarioValues = useMemo(
    () => ({
      proprietario: pastaAtual?.proprietario?.email ?? ''
    }),
    [pastaAtual]
  )

  const form = useForm<AlterarProprietarioValues>({
    values: initialValues,
    resolver: zodResolver(alterarProprietarioSchema),
    mode: 'onSubmit'
  })

  const onSubmit: SubmitHandler<AlterarProprietarioValues> = useCallback(
    async values => {
      const alterarProprietarioInput: AlterarProprietarioInput = {
        emailProprietario: values.proprietario,
        idPasta: pastaAtual.id
      }

      try {
        await alterarProprietarioMutation.mutateAsync({
          alterarProprietarioInput
        })

        snackbar.show('success', t`Acesso concedido com sucesso!`)

        form.reset()
        close()
      } catch (error) {
        const message = getGraphQLErrorMessage(error)
        snackbar.show('error', message ?? t`Erro ao conceder acesso :(`)
        console.error(message)
      }
    },
    [pastaAtual, snackbar, form, close]
  )

  const handleCloseForm = useCallback(() => {
    form.reset()
    close()
  }, [form, close])

  return {
    form,
    onSubmit,
    handleCloseForm
  }
}
